import { Component, Input, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucidePlus,
  lucideSearch,
  lucideX,
  lucideChevronRight,
  lucidePencil,
  lucideList,
  lucideCodeXml,
  lucideServer,
} from '@ng-icons/lucide';

import { UI, type UiBadgeVariant } from '../../components/ui';
import type { EntityConfig } from '../../config/entity-config';
import { ProviderService, type APIProvider } from '../../services/provider.service';
import { ShellRoutesService } from '../../services/shell-routes.service';
import { ProviderFormComponent, emptyProviderForm, type ProviderFormValue } from './provider-form.component';

type ProviderTypeFilter = '' | 'FINANCIAL_ENTITY' | 'AGGREGATOR' | 'GATEWAY' | 'PROXY';
type ActiveFilter = '' | 'true' | 'false';

/** Represents a single key-value row in the editor. */
interface KvRow {
  key: string;
  value: string;
}

/**
 * Providers list with cards (navigate to hub), filters, and add/edit modal.
 */
@Component({
  selector: 'app-providers-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, NgIcon, ProviderFormComponent, ...UI],
  templateUrl: './providers-page.component.html',
  viewProviders: [
    provideIcons({
      lucidePlus,
      lucideSearch,
      lucideX,
      lucideChevronRight,
      lucidePencil,
      lucideList,
      lucideCodeXml,
      lucideServer,
    }),
  ],
})
export class ProvidersPageComponent implements OnInit {
  @Input() entityConfig!: EntityConfig;

  private readonly providerService = inject(ProviderService);
  readonly shellRoutes = inject(ShellRoutesService);

  readonly providers = signal<APIProvider[]>([]);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly success = signal<string | null>(null);

  /** Filters */
  searchQuery = '';
  typeFilter: ProviderTypeFilter = '';
  activeFilter: ActiveFilter = '';

  /** Modal state */
  readonly modalOpen = signal(false);
  readonly modalSaving = signal(false);
  modalEditId: string | null = null;
  modalForm: ProviderFormValue = emptyProviderForm();
  modalConfigMode: 'kv' | 'json' = 'kv';
  modalConfigKvRows: KvRow[] = [{ key: '', value: '' }];
  modalConfigJson = '{}';

  /** Lookup labels shown by the form when editing an existing provider */
  entityLabel = '';
  countryLabel = '';
  currencyLabel = '';

  ngOnInit(): void {
    this.loadProviders();
  }

  // ─── Providers list ─────────────────────────────────────────

  loadProviders(): void {
    this.loading.set(true);
    this.error.set(null);

    const params: Record<string, string | number | boolean | undefined> = {};
    if (this.typeFilter) params['provider_type'] = this.typeFilter;
    if (this.activeFilter) params['is_active'] = this.activeFilter;
    if (this.searchQuery) params['search'] = this.searchQuery;

    this.providerService.list(params).subscribe({
      next: (res) => {
        this.providers.set(res.results);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err?.error?.detail ?? err?.message ?? 'Failed to load providers.');
        this.loading.set(false);
      },
    });
  }

  onFilterChange(): void {
    this.loadProviders();
  }

  onSearchClear(): void {
    this.searchQuery = '';
    this.loadProviders();
  }

  toggleActive(provider: APIProvider, event: Event): void {
    event.stopPropagation();
    const action$ = provider.is_active
      ? this.providerService.deactivate(provider.id)
      : this.providerService.activate(provider.id);

    action$.subscribe({
      next: (updated) => {
        this.providers.update((list) => list.map((p) => (p.id === updated.id ? updated : p)));
      },
      error: (err) => {
        this.error.set(err?.error?.detail ?? err?.message ?? 'Failed to toggle status.');
      },
    });
  }

  // ─── Provider Modal (Add / Edit) ────────────────────────────

  openAddModal(): void {
    this.modalEditId = null;
    this.modalForm = emptyProviderForm();
    this.modalConfigMode = 'kv';
    this.modalConfigKvRows = [{ key: '', value: '' }];
    this.modalConfigJson = '{}';
    this.entityLabel = '';
    this.countryLabel = '';
    this.currencyLabel = '';
    this.error.set(null);
    this.modalOpen.set(true);
  }

  openEditModal(provider: APIProvider): void {
    this.modalEditId = provider.id;
    this.modalForm = {
      code: provider.code,
      name: provider.name,
      provider_type: provider.provider_type,
      adapter_class: provider.adapter_class ?? '',
      financial_entity: provider.financial_entity,
      country: provider.country,
      currency: provider.currency,
      is_active: provider.is_active,
    };
    this.modalConfigMode = 'kv';
    this.modalConfigKvRows = this.jsonToKvRows(provider.configuration ?? {});
    this.modalConfigJson = JSON.stringify(provider.configuration ?? {}, null, 2);

    this.entityLabel = provider.financial_entity_code
      ? `${provider.financial_entity_code} — ${provider.financial_entity_name ?? ''}`
      : '';
    this.countryLabel = provider.country_code
      ? `${provider.country_code} — ${provider.country_name ?? ''}`
      : '';
    this.currencyLabel = provider.currency_code ?? '';

    this.error.set(null);
    this.modalOpen.set(true);
  }

  closeModal(): void {
    this.modalOpen.set(false);
  }

  switchModalConfigMode(mode: 'kv' | 'json'): void {
    if (mode === this.modalConfigMode) return;

    if (mode === 'json') {
      this.modalConfigJson = JSON.stringify(this.kvRowsToJson(this.modalConfigKvRows), null, 2);
    } else {
      try {
        const parsed = JSON.parse(this.modalConfigJson);
        this.modalConfigKvRows = this.jsonToKvRows(parsed);
      } catch {
        this.error.set('Invalid JSON — cannot switch to Key/Value mode.');
        return;
      }
    }
    this.modalConfigMode = mode;
  }

  addModalConfigRow(): void {
    this.modalConfigKvRows = [...this.modalConfigKvRows, { key: '', value: '' }];
  }

  removeModalConfigRow(index: number): void {
    this.modalConfigKvRows = this.modalConfigKvRows.filter((_, i) => i !== index);
  }

  saveModal(): void {
    if (!this.modalForm.code.trim() || !this.modalForm.name.trim()) {
      this.error.set('Code and Name are required.');
      return;
    }

    let config: Record<string, unknown>;
    if (this.modalConfigMode === 'kv') {
      config = this.kvRowsToJson(this.modalConfigKvRows);
    } else {
      try {
        config = JSON.parse(this.modalConfigJson);
      } catch {
        this.error.set('Invalid JSON in configuration.');
        return;
      }
    }

    const payload: Partial<APIProvider> = {
      code: this.modalForm.code.trim(),
      name: this.modalForm.name.trim(),
      provider_type: this.modalForm.provider_type,
      adapter_class: this.modalForm.adapter_class.trim() || null,
      financial_entity: this.modalForm.financial_entity,
      country: this.modalForm.country,
      currency: this.modalForm.currency,
      is_active: this.modalForm.is_active,
      configuration: config,
    };

    this.modalSaving.set(true);
    this.error.set(null);

    const action$ = this.modalEditId
      ? this.providerService.update(this.modalEditId, payload)
      : this.providerService.create(payload);

    action$.subscribe({
      next: (saved) => {
        if (this.modalEditId) {
          this.providers.update((list) => list.map((p) => (p.id === saved.id ? saved : p)));
        } else {
          this.providers.update((list) => [saved, ...list]);
        }
        this.modalSaving.set(false);
        this.closeModal();
        this.success.set(this.modalEditId ? 'Provider updated.' : 'Provider created.');
        setTimeout(() => this.success.set(null), 3000);
      },
      error: (err) => {
        const detail = err?.error?.detail ?? err?.error?.code?.[0] ?? err?.message ?? 'Failed to save provider.';
        this.error.set(typeof detail === 'string' ? detail : JSON.stringify(detail));
        this.modalSaving.set(false);
      },
    });
  }

  // ─── K/V utility helpers ────────────────────────────────────

  private jsonToKvRows(obj: Record<string, unknown>): KvRow[] {
    const entries = Object.entries(obj);
    if (entries.length === 0) return [{ key: '', value: '' }];
    return entries.map(([key, value]) => ({
      key,
      value: typeof value === 'string' ? value : JSON.stringify(value),
    }));
  }

  private kvRowsToJson(rows: KvRow[]): Record<string, unknown> {
    const result: Record<string, unknown> = {};
    for (const row of rows) {
      const k = row.key.trim();
      if (!k) continue;
      try {
        result[k] = JSON.parse(row.value);
      } catch {
        result[k] = row.value;
      }
    }
    return result;
  }

  getDashIconClasses(type: string): string {
    switch (type) {
      case 'FINANCIAL_ENTITY':
        return 'bg-info-bg text-info-fg';
      case 'AGGREGATOR':
        return 'bg-success-bg text-success-fg';
      case 'GATEWAY':
        return 'bg-warning-bg text-warning-fg';
      case 'PROXY':
        return 'bg-row-hover text-brand';
      default:
        return 'bg-surface-muted text-fg-secondary';
    }
  }

  getProviderTypeVariant(type: string): UiBadgeVariant {
    switch (type) {
      case 'FINANCIAL_ENTITY':
        return 'info';
      case 'AGGREGATOR':
        return 'success';
      case 'GATEWAY':
        return 'warning';
      case 'PROXY':
        return 'brand';
      default:
        return 'neutral';
    }
  }

  getProviderTypeLabel(type: string): string {
    switch (type) {
      case 'FINANCIAL_ENTITY':
        return 'Financial Entity';
      case 'AGGREGATOR':
        return 'Aggregator';
      case 'GATEWAY':
        return 'Gateway';
      case 'PROXY':
        return 'Proxy';
      default:
        return type;
    }
  }
}
